// Приём файлов из ОС в композер (SPEC §4.2, композер-вложения). Два канала, единый выход — колбэк
// onFiles (→ composer.addFiles, тот же путь, что скрепка):
//   • ВСТАВКА из буфера (paste) — mobile + desktop. Слушаем на document: событие всплывает из поля
//     (вставка в textarea), а на desktop ловим и Ctrl+V без фокуса в поле. Реагируем ТОЛЬКО когда в
//     буфере есть файлы — обычная вставка текста проходит штатно (files пуст → не вмешиваемся).
//   • ПЕРЕТАСКИВАНИЕ (drag&drop) — только desktop (на touch нет DnD). Отдаём dropProps на контейнер-зону
//     (поле ввода + лента) и isDragging для оверлея-подсказки.
// Файлы извлекаем и из DataTransfer.files, и из .items[kind==='file'] (разные источники кладут по-разному).

import { useCallback, useEffect, useRef, useState, type DragEventHandler } from 'react'

/** Минимальный контракт DataTransfer/ClipboardData — чтобы извлечь File[] из paste и из drop. */
interface TransferLike {
  files?: ArrayLike<File> & { item?: (i: number) => File | null }
  items?: ArrayLike<{ kind: string; getAsFile?: () => File | null }>
  types?: ArrayLike<string>
}

/** File[] из DataTransfer: сперва .files (основной путь везде), иначе .items[kind==='file'] (fallback). */
function filesFromTransfer(dt: TransferLike | null | undefined): File[] {
  if (!dt) return []
  const out: File[] = []
  const fl = dt.files
  if (fl && fl.length > 0) {
    for (let i = 0; i < fl.length; i += 1) {
      const f = typeof fl.item === 'function' ? fl.item(i) : fl[i]
      if (f) out.push(f)
    }
  }
  if (out.length === 0 && dt.items && dt.items.length > 0) {
    for (let i = 0; i < dt.items.length; i += 1) {
      const it = dt.items[i]
      if (it && it.kind === 'file') {
        const f = it.getAsFile?.()
        if (f) out.push(f)
      }
    }
  }
  return out
}

/** Перетаскивают именно файлы (а не текст/ссылку/внутренний drag) — по наличию 'Files' в types. */
function draggingFiles(dt: TransferLike | null | undefined): boolean {
  if (!dt || !dt.types) return false
  // types бывает DOMStringList (без .includes) — приводим к массиву.
  for (let i = 0; i < dt.types.length; i += 1) if (dt.types[i] === 'Files') return true
  return false
}

// Текстовые представления буфера — их пропускаем при async-чтении (нам нужны файлы/картинки).
const CLIPBOARD_TEXT_TYPES = new Set(['text/plain', 'text/html', 'text/uri-list'])

/**
 * Async Clipboard API доступен (navigator.clipboard.read + конструктор ClipboardItem). Это ЕДИНСТВЕННЫЙ
 * надёжный путь достать картинку из буфера на iOS Safari: в обычный <textarea> WebKit файл из вставки
 * не кладёт (текстовое поле), а read() отдаёт image/png по жесту-тапу.
 */
export function clipboardReadSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard !== 'undefined' &&
    typeof navigator.clipboard.read === 'function' &&
    typeof ClipboardItem !== 'undefined'
  )
}

/** Расширения по MIME для «безымянных» файлов из буфера (когда read() отдаёт только тип). */
const EXT_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/tiff': 'tiff',
}

/**
 * Читает файлы из буфера через async Clipboard API (image/png и пр.). Из каждого ClipboardItem берём
 * первый НЕтекстовый тип. Возвращает и сами файлы, и ПЛОСКИЙ список всех увиденных типов (для диагностики
 * пустого случая — на iOS фото из «Галереи» может приходить не байтами, а текстовым референсом).
 * Исключение (NotAllowedError при отказе пользователя) пробрасывается. Вызывать в рамках жеста (тап/Ctrl+V).
 */
export async function readClipboard(): Promise<{ files: File[]; types: string[] }> {
  if (!clipboardReadSupported()) return { files: [], types: [] }
  const items = await navigator.clipboard.read()
  const files: File[] = []
  const types: string[] = []
  for (const item of items) {
    for (const t of item.types) if (!types.includes(t)) types.push(t)
    const type = item.types.find((t) => !CLIPBOARD_TEXT_TYPES.has(t))
    if (type === undefined) continue
    const blob = await item.getType(type)
    const ext = EXT_BY_MIME[type] ?? type.split('/')[1]?.split(/[;+]/)[0] ?? 'bin'
    files.push(new File([blob], `paste-${Date.now()}-${files.length + 1}.${ext}`, { type }))
  }
  return { files, types }
}

export interface FileIntakeDropProps {
  onDragEnter?: DragEventHandler<HTMLElement>
  onDragOver?: DragEventHandler<HTMLElement>
  onDragLeave?: DragEventHandler<HTMLElement>
  onDrop?: DragEventHandler<HTMLElement>
}

export interface FileIntake {
  /** Над зоной сейчас тащат файлы (desktop) — для показа оверлея-подсказки. */
  isDragging: boolean
  /** Пропсы drag&drop для контейнера-зоны. На mobile (dragEnabled=false) — пустые. */
  dropProps: FileIntakeDropProps
}

export interface UseFileIntakeOptions {
  /** Единый приёмник файлов (обычно composer.addFiles). */
  onFiles: (files: File[]) => void
  /** Включать перетаскивание только на desktop (на touch DnD нет). Вставка (paste) работает всегда. */
  dragEnabled: boolean
}

/**
 * Приём файлов из буфера (вставка) и перетаскиванием (desktop). Возвращает пропсы drop-зоны и флаг
 * перетаскивания; вставку вешает на document сам (снимает слушатель при размонтировании).
 */
export function useFileIntake(opts: UseFileIntakeOptions): FileIntake {
  const { onFiles, dragEnabled } = opts
  // onFiles может менять идентичность между рендерами — держим свежую ссылку, чтобы не переподписывать
  // document-слушатель на каждый рендер.
  const onFilesRef = useRef(onFiles)
  onFilesRef.current = onFiles

  // — Вставка из буфера (mobile + desktop) —
  useEffect(() => {
    const onPaste = (e: ClipboardEvent): void => {
      const files = filesFromTransfer(e.clipboardData as unknown as TransferLike | null)
      if (files.length > 0) {
        // Есть файлы в событии (desktop/Android) → вставка вложения, а не текста: гасим дефолт (иначе
        // картинка могла бы вставиться ещё и как data-URL/имя в поле) и отдаём в композер.
        e.preventDefault()
        onFilesRef.current(files)
        return
      }
      // Файлов в событии нет. Если есть текст — это обычная вставка текста, не мешаем.
      const text =
        typeof e.clipboardData?.getData === 'function' ? e.clipboardData.getData('text/plain') : ''
      if (text.length > 0) return
      // Пустой paste — частый случай iOS: картинку в <textarea> WebKit в событие не кладёт. Раз мы уже
      // в жесте вставки, пробуем async-чтение буфера (image/png). Тихо игнорируем отказ/пустоту.
      if (clipboardReadSupported()) {
        e.preventDefault()
        void readClipboard()
          .then(({ files: fs }) => {
            if (fs.length > 0) onFilesRef.current(fs)
          })
          .catch(() => undefined)
      }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [])

  // — Перетаскивание (desktop) —
  // Счётчик глубины гасит ложные dragleave: при движении курсора между дочерними узлами зоны браузер
  // шлёт dragleave старого + dragenter нового; без счётчика оверлей мигал бы.
  const depth = useRef(0)
  const [isDragging, setDragging] = useState(false)

  const onDragEnter = useCallback<DragEventHandler<HTMLElement>>((e) => {
    if (!draggingFiles(e.dataTransfer)) return
    e.preventDefault()
    depth.current += 1
    setDragging(true)
  }, [])

  const onDragOver = useCallback<DragEventHandler<HTMLElement>>((e) => {
    if (!draggingFiles(e.dataTransfer)) return
    // preventDefault ОБЯЗАТЕЛЕН на dragover, иначе браузер не выдаст drop; dropEffect=copy → курсор «+».
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }, [])

  const onDragLeave = useCallback<DragEventHandler<HTMLElement>>((e) => {
    if (!draggingFiles(e.dataTransfer)) return
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setDragging(false)
  }, [])

  const onDrop = useCallback<DragEventHandler<HTMLElement>>((e) => {
    if (!draggingFiles(e.dataTransfer)) return
    e.preventDefault()
    depth.current = 0
    setDragging(false)
    const files = filesFromTransfer(e.dataTransfer as unknown as TransferLike | null)
    if (files.length > 0) onFilesRef.current(files)
  }, [])

  return {
    isDragging: dragEnabled && isDragging,
    dropProps: dragEnabled ? { onDragEnter, onDragOver, onDragLeave, onDrop } : {},
  }
}
